import { useSelector } from 'react-redux';

import { selectDealType, selectOfferType } from 'shared/selectors/jsonQuery';
import { selectLocation } from 'shared/selectors/location';
import { selectRegions } from 'shared/selectors/regions';
import { FC, useCallback, useMemo, useEffect } from 'react';
import { getBoundedBy } from './utils';
import { useAppDispatch, useModalState } from 'shared/hooks';
import { GeoSwitcherButtonsContainer } from './containers/GeoSwitcherButtonsContainer';
import { IGeoObject, IPolygonObject, ISelectKPParams } from 'shared/types/geoSuggest';
import { changeBuilder, selectGeo, selectKP } from 'shared/thunks/geo/geoSuggest';
import { selectDirectionsModalData, selectIsGeoModalOpen } from 'shared/selectors/geo';
import { GeoSuggest } from '../../../packages/GeoComponents';
import { applyFilters } from 'shared/thunks/filters';
import { selectHighways } from 'shared/thunks/geo/directions';
import { updateTags } from 'shared/thunks/tags';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { setSearchMetaTooltipAvailable, updateLastAppliedFilter } from 'shared/slices/searchMetaTooltip';
import { GeoSuggestV2 } from 'shared/components/GeoSuggestV2';
import { selectIsInlineFiltersRedesigned } from 'shared/selectors/redesigned/selectIsInlineFiltersRedesigned';
import { selectGeoTypeFilter } from 'shared/selectors/filters/geo/selectGeoTypeFilter';
import { GeoSuggestModalContainer } from '../GeoSuggestModalContainer';
import { closeGeoModal } from 'shared/slices/geo/slice';

export const GeoSuggestContainer: FC = () => {
  const {
    custom: { applyFilterAfterChange },
  } = useApplicationContext();
  const dispatch = useAppDispatch();
  const dealType = useSelector(selectDealType);
  const offerType = useSelector(selectOfferType);
  const highwaysData = useSelector(selectDirectionsModalData);
  const currentLocation = useSelector(selectLocation);
  const regions = useSelector(selectRegions);

  const boundedBy = useMemo(() => getBoundedBy(currentLocation, regions), [currentLocation, regions]);

  const onChange = useCallback(() => {
    if (applyFilterAfterChange) {
      dispatch(applyFilters());
    } else {
      dispatch(setSearchMetaTooltipAvailable(true));
      dispatch(updateLastAppliedFilter('geo'));
    }
  }, [applyFilterAfterChange, dispatch]);

  const handleGeoSelect = useCallback(
    async (value: IGeoObject | IPolygonObject, userInput: string) => {
      await dispatch(selectGeo({ value, userInput }));

      if (value.type === 'location' || value.type === 'regional_district_id') {
        dispatch(applyFilters());
      } else {
        dispatch(updateTags(true));
        onChange();
      }
    },
    [dispatch, onChange],
  );

  const handleHighwaySelect = useCallback(
    async (highwaysIds: number[], regionId?: number) => {
      await dispatch(selectHighways({ highwaysIds, regionId }));
      dispatch(updateTags(true));
      onChange();
    },
    [dispatch, onChange],
  );

  const handleKPSelect = useCallback(
    async (params: ISelectKPParams) => {
      await dispatch(selectKP(params));
      dispatch(updateTags(true));
      onChange();
    },
    [dispatch, onChange],
  );

  const handleBuilderSelect = useCallback(
    async (params: { builderId: number; name: string }) => {
      await dispatch(changeBuilder({ id: params.builderId, text: params.name }));
      dispatch(updateTags(true));
      onChange();
    },
    [dispatch, onChange],
  );

  const isFiltersRedesignEnabled = useSelector(selectIsInlineFiltersRedesigned);
  const { label, tabItems } = useSelector(selectGeoTypeFilter);
  const isModalOpened = useSelector(selectIsGeoModalOpen);

  const { opened, onClose, onOpen } = useModalState();

  const handleClose = useCallback(() => {
    onClose();
    closeGeoModal();
  }, [onClose]);

  useEffect(() => {
    if (isModalOpened) {
      onOpen();
    }
  }, [isModalOpened, onOpen]);

  return isFiltersRedesignEnabled ? (
    <GeoSuggestV2 label={label} onOpen={onOpen}>
      {opened && (
        <GeoSuggestModalContainer
          onModalClose={handleClose}
          tabItems={tabItems}
          geoSuggestNode={
            <GeoSuggest
              isRedesign={true}
              highwaysData={highwaysData}
              dealType={dealType}
              offerType={offerType}
              currentLocation={currentLocation}
              boundedBy={boundedBy}
              onGeoSelected={handleGeoSelect}
              onHighwaySelected={handleHighwaySelect}
              onKPSelected={handleKPSelect}
              onBuilderSelected={handleBuilderSelect}
            />
          }
        />
      )}
    </GeoSuggestV2>
  ) : (
    <GeoSuggest
      rightAddon={<GeoSwitcherButtonsContainer />}
      highwaysData={highwaysData}
      dealType={dealType}
      offerType={offerType}
      currentLocation={currentLocation}
      boundedBy={boundedBy}
      onGeoSelected={handleGeoSelect}
      onHighwaySelected={handleHighwaySelect}
      onKPSelected={handleKPSelect}
      onBuilderSelected={handleBuilderSelect}
    />
  );
};
